import { feedbackTriagePath } from '../lib/router'

/**
 * Two userhub links must survive sign-in:
 *  - the link in a userhub email: `/?feedback=tickets&ticket=<id>` opens My tickets on that ticket;
 *  - the link the devops daemon writes into every ADO work item it files for a user ticket:
 *    `/feedback-triage?ticket=<id>` opens the triage page on that ticket (AB#374).
 * The widget reads them ONCE, when it mounts — which is after sign-in. A signed-out member goes
 * through `loginRedirect`, which returns to the app origin and drops the path and the query, so
 * AuthGate stashes the link before signing in and puts it back in the URL just before the signed-in
 * shell (and with it the router and the widget) mounts.
 *
 * The stash is a query string (`feedback=…`, merged into the URL) or, for the triage page, a path
 * starting with `/` (replacing the URL).
 */
const KEY = 'niobe.userhub.link'
const PARAMS = ['feedback', 'ticket'] as const

function isTriagePath(pathname: string): boolean {
  return pathname.replace(/\/+$/, '') === feedbackTriagePath
}

/** Remember the feedback query or the triage link, if the page was opened with one. */
export function stashFeedbackLink(): void {
  const url = new URL(window.location.href)
  let link: string
  if (isTriagePath(url.pathname)) {
    const ticket = url.searchParams.get('ticket')
    link = feedbackTriagePath + (ticket ? '?' + new URLSearchParams({ ticket }).toString() : '')
  } else {
    if (!url.searchParams.has('feedback')) return
    const kept = new URLSearchParams()
    for (const p of PARAMS) {
      const v = url.searchParams.get(p)
      if (v !== null) kept.set(p, v)
    }
    link = kept.toString()
  }
  try {
    sessionStorage.setItem(KEY, link)
  } catch {
    /* storage unavailable — the link then just opens the app */
  }
}

/** Put a stashed link back in the URL (unless it is still there), then forget it. */
export function restoreFeedbackLink(): void {
  let stashed: string | null = null
  try {
    stashed = sessionStorage.getItem(KEY)
    sessionStorage.removeItem(KEY)
  } catch {
    return
  }
  if (!stashed) return
  const url = new URL(window.location.href)
  if (stashed.startsWith('/')) {
    if (isTriagePath(url.pathname)) return
    window.history.replaceState(window.history.state, '', stashed)
    return
  }
  if (url.searchParams.has('feedback')) return
  for (const [k, v] of new URLSearchParams(stashed)) url.searchParams.set(k, v)
  window.history.replaceState(window.history.state, '', url.toString())
}
